import {
  ALWAYS_ALLOWED_BLOCK_TYPES,
  PACKAGE_TIER_KEY_VALUES,
  resolveBlockTypeForPresentation,
  type PackageTierKey,
  type RegistryBlockKey,
  type RegistryBlockType,
  isRegistryBlockType,
} from '../catalog/platformCatalogIds';
import { isSystemPageComponentType } from '../content/legalBlockFields';
import type { LayoutComponentType } from '../content/studio';
import { COMPONENT_TYPE_REGISTRY_KEYS } from './blockImplementation';
import { getVerticalDefinition, PLATFORM_VERTICAL_MATRIX } from './platformVerticalMatrix';

export type BlockTierAccess = 'active' | 'frozen';

/** Tier allowed types ∪ always-allowed legal types. */
export function getEffectiveAllowedBlockTypes(
  vertical: string,
  tier: string,
): RegistryBlockType[] {
  const definition = PLATFORM_VERTICAL_MATRIX[vertical as keyof typeof PLATFORM_VERTICAL_MATRIX];
  if (!definition || !(PACKAGE_TIER_KEY_VALUES as readonly string[]).includes(tier)) {
    return [...ALWAYS_ALLOWED_BLOCK_TYPES];
  }
  const allowed = definition.tiers[tier as PackageTierKey]?.allowedBlocks ?? [];
  const merged = new Set<RegistryBlockType>([...allowed, ...ALWAYS_ALLOWED_BLOCK_TYPES]);
  return [...merged];
}

/**
 * Whether a block type or presentation key is allowed for the vertical/tier.
 * Presentations are allowed when their parent type is entitled.
 */
export function isBlockAllowed(vertical: string, tier: string, blockKey: string): boolean {
  const effective = getEffectiveAllowedBlockTypes(vertical, tier);
  if (isRegistryBlockType(blockKey)) {
    return effective.includes(blockKey);
  }
  const blockType = resolveBlockTypeForPresentation(blockKey);
  return blockType ? effective.includes(blockType) : false;
}

export function getRegistryKeyForComponentType(componentType: string): RegistryBlockKey {
  return (
    COMPONENT_TYPE_REGISTRY_KEYS[componentType as LayoutComponentType] ??
    (componentType as RegistryBlockKey)
  );
}

function resolveRegistryBlockKey(
  componentType: string,
  registryBlockKey?: string,
): RegistryBlockKey {
  return (registryBlockKey as RegistryBlockKey | undefined) ?? getRegistryKeyForComponentType(componentType);
}

/** Whether a block is editable for the project's vertical + package tier (v4.1 §5.2 Zone 2). */
export function getBlockTierAccess(
  vertical: string,
  tier: string,
  componentType: string,
  registryBlockKey?: string,
): BlockTierAccess {
  if (isSystemPageComponentType(componentType)) {
    return 'active';
  }
  const registryKey = resolveRegistryBlockKey(componentType, registryBlockKey);
  return isBlockAllowed(vertical, tier, registryKey) ? 'active' : 'frozen';
}

/** Tier access for a layout block (uses `registryBlockKey` when bootstrap stored the matrix key). */
export function getBlockTierAccessForBlock(
  vertical: string,
  tier: string,
  block: { componentType: string; registryBlockKey?: string },
): BlockTierAccess {
  return getBlockTierAccess(vertical, tier, block.componentType, block.registryBlockKey);
}

export function getAllowedBlocksForTier(vertical: string, tier: string): RegistryBlockType[] {
  return getEffectiveAllowedBlockTypes(vertical, tier).filter(
    (t) => !(ALWAYS_ALLOWED_BLOCK_TYPES as readonly string[]).includes(t),
  );
}

export function getOptionalAddonBlocksForTier(vertical: string, tier: string): RegistryBlockType[] {
  const definition = getVerticalDefinition(vertical);
  if (!definition || !(PACKAGE_TIER_KEY_VALUES as readonly string[]).includes(tier)) {
    return [];
  }
  const tierConfig = definition.tiers[tier as PackageTierKey];
  const addons = tierConfig?.optionalAddonBlocks ?? [];
  return addons.filter((key) => tierConfig.allowedBlocks.includes(key));
}
